import { type ILog, Log } from '@servicetitan/log-service';
import { inject, injectable, symbolToken } from '@servicetitan/react-ioc';
import { action, makeObservable, observable } from 'mobx';
import { ApiModels, type IMainApi, MAIN_API_TOKEN } from '../../api';

export const SETTINGS_STORE_TOKEN = symbolToken<ISettingsStore>('SETTINGS_STORE_TOKEN');

export interface ISettingsStore {
    isLoaded: boolean;
    settings?: ApiModels.AppSettings;
    loadSettings(): Promise<ApiModels.AppSettings | undefined>;
}

@injectable()
export class SettingsStore implements ISettingsStore {
    @observable settings?: ApiModels.AppSettings;

    @observable.ref settingsLoadingPromise?: Promise<ApiModels.AppSettings>;

    @observable isLoaded = false;

    constructor(
        @inject(Log) private readonly log: ILog,
        @inject(MAIN_API_TOKEN) private readonly mainApi: IMainApi
    ) {
        makeObservable(this);
    }

    async loadSettings() {
        if (this.settingsLoadingPromise) {
            return this.settingsLoadingPromise;
        }
        try {
            this.settingsLoadingPromise = this.mainApi.getSettings();
            const settings = await this.settingsLoadingPromise;
            this.setSettings(settings);
            this.setLoaded(true);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            this.log.error({
                category: 'ISH FE',
                code: 'LoadSettingsFailed',
                message,
                error,
            });
        }
    }

    @action
    private setLoaded(loaded: boolean) {
        this.isLoaded = loaded;
    }

    @action
    private setSettings(settings: ApiModels.AppSettings) {
        this.settings = settings;
    }
}
